import produce from 'immer';
import { get, isArray, omit, template } from 'lodash';

import type { RequestError } from '@swf/core/api/request';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type {
  FetchServiceMeta,
  ReducerPayload,
  ServiceParams,
  UpdatePayload,
  WidgetContainer,
  WidgetData,
  WidgetReducer
} from '@swf/core/types/Widget';
import { canUseDOM } from '@swf/core/utils';

import { fetchProperties } from '../utils/widgets';

interface ResponsePayload extends ReducerPayload {
  reducer?: WidgetReducer;
}

export interface ClearPayload {
  id: string;
  key?: string | number;
}

export interface WidgetState {
  [id: string]: WidgetData;
}

interface WidgetsModel extends ModelConfig<WidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const reducers = {
  response: (state: WidgetState, payload: ResponsePayload): WidgetState =>
    produce(state, (draft) => {
      if (payload.reducer) {
        draft[payload.id] = payload.reducer(state[payload.id], omit(payload, 'reducer'));
      } else {
        draft[payload.id] = draft[payload.id] || {};
        draft[payload.id][payload.key] = payload.data;
      }
    }),
  clear: (state: WidgetState, { id, key }: ClearPayload): WidgetState =>
    produce(state, (draft) => {
      if (isArray(draft[id]) && typeof key === 'number') {
        draft[id].splice(key, 1);
      } else if (draft[id] && key) {
        delete draft[id][key];
      } else {
        delete draft[id];
      }
    }),
  update: (state: WidgetState, payload: UpdatePayload): WidgetState =>
    produce(state, (draft) => {
      const { reducer } = payload;
      draft[payload.id] = reducer(state[payload.id]);
    })
};

const effects = {
  async fetchData(
    options: {
      key: string;
      widgetConfig: WidgetContainer;
      meta?: FetchServiceMeta;
      params?: ServiceParams;
    },
    { rootState, dispatch, request }: EffectParams
  ) {
    const { key, widgetConfig, meta = {} } = options;
    const { config, services = {}, id, type } = widgetConfig;
    const { serviceReducers, serviceErrorReducers, errorHandler } = await fetchProperties(type);

    const reducer = meta.reducer || get(serviceReducers, key);
    const serviceConfig = services[key];
    const values = meta.templateValues || serviceConfig.config.templateValues || {};
    const name = template(serviceConfig.config.name)(values);
    const params = options.params || serviceConfig.params;

    try {
      const { body: data } = await request({
        service: name,
        body: params,
        method: serviceConfig.config.method
      });
      dispatch.widgets.response({ data, reducer, id, key, config, meta });
    } catch (error) {
      const errorReducer = get(serviceErrorReducers, key);
      if (errorHandler && canUseDOM) {
        const currentData = select.widgets.getData(rootState, id);
        const data = await errorHandler(currentData, { params, id, key, config, error, meta: {} });
        dispatch.widgets.response({ data, id, key, config, meta: {} });
      } else if (errorReducer) {
        const { headers, body: data } = error as RequestError;
        dispatch.widgets.response({
          id,
          key,
          config,
          data,
          reducer: errorReducer,
          meta: { statusCode: headers?.statusCode }
        });
      }
    }
  }
};

const selectors = (slice: Slicer<WidgetState>, createSelector: SelectorCreator) => ({
  getData() {
    return createSelector(
      slice,
      (rootState: RootState, id: string) => id,
      (state, id): WidgetData => state[id]
    );
  }
});

const widgets: WidgetsModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export default widgets;
